<template>
	<v-expansion-panels :model-value="expanded ? panelKey : undefined" @update:model-value="$emit('update:expanded', $event === panelKey)">
		<v-expansion-panel :value="panelKey" class="widget-builder--panel">
			<v-expansion-panel-title>
				<v-icon>{{ icon }}</v-icon>
				<div class="order-tracking-panel__heading">
					<span class="order-tracking-panel__title">{{ title }}</span>
					<span class="order-tracking-panel__description">{{ description }}</span>
				</div>
			</v-expansion-panel-title>
			<v-expansion-panel-text>
				<ChartCard v-if="chart === CHART_KIND.EVOLUTION" class="metric-analysis__chart" :options="evolutionOptions" :is-empty="isEvolutionEmpty" :scrollable="!stacked" :file-name="panelKey">
					<template v-if="stacked" #subheader>
						<v-chip v-if="selectedBar" size="small" variant="tonal" closable @click:close="selectedBar = null">
							{{ selectedBar.month }} · {{ selectedBar.label }}
							<strong class="ml-1">{{ selectedBar.formatted }}</strong>
						</v-chip>
					</template>
				</ChartCard>

				<ChartCard v-else-if="isDonut" class="metric-analysis__chart" :options="distributionOptions" :is-empty="categories.length === 0" :file-name="panelKey">
					<template #subheader>
						<SelectControl :model-value="level" :items="levelOptions" title-key="label" item-value="value" :label="t('CategoryLevel')" :sort-items="false" :clearable="false" density="compact" class="metric-analysis__level" @update:model-value="$emit('update:level', $event)" />
					</template>
					<template #aside>
						<CategoryBreakdownList :rows="categoryRows" variant="legend" />
					</template>
				</ChartCard>

				<v-card v-else class="metric-analysis__chart" flat>
					<v-card-title class="metric-analysis__list-title">
						<SelectControl :model-value="level" :items="levelOptions" title-key="label" item-value="value" :label="t('CategoryLevel')" :sort-items="false" :clearable="false" density="compact" class="metric-analysis__level" @update:model-value="$emit('update:level', $event)" />
					</v-card-title>

					<v-card-text>
						<CategoryBreakdownList :rows="categoryRows" variant="bars" />
					</v-card-text>
				</v-card>
			</v-expansion-panel-text>
		</v-expansion-panel>
	</v-expansion-panels>
</template>

<script setup>
import { ref, computed, watch, defineAsyncComponent } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
const ChartCard = defineAsyncComponent(() => import('@/components/common/ChartCard.vue'));
import { CHART_KIND, buildEvolutionOptions, buildDistributionOptions, buildCategoryRows, isDistributionDonut } from '@/components/orderTracking/orderTrackingCharts';
import CategoryBreakdownList from '@/components/orderTracking/CategoryBreakdownList.vue';
import SelectControl from '@/components/controls/SelectControl.vue';
import { METRIC } from '@/services/directOrderService';

const props = defineProps({
	chart: { type: String, default: CHART_KIND.EVOLUTION },
	title: { type: String, default: '' },
	description: { type: String, default: '' },
	icon: { type: String, default: 'insights' },
	panelKey: { type: String, default: 'metric' },
	metric: { type: String, default: METRIC.VALUE },
	monthlySeries: { type: Array, default: () => [] },
	categories: { type: Array, default: () => [] },
	level: { type: Number, default: 1 },
	levelOptions: { type: Array, default: () => [] },
	showReturns: { type: Boolean, default: false },
	stacked: { type: Boolean, default: false },
	expanded: { type: Boolean, default: false },
});

defineEmits(['update:expanded', 'update:level']);

const t = useTranslator();
const isEvolutionEmpty = computed(() => props.monthlySeries.every((month) => month.orderCount === 0));
const selectedBar = ref(null);
watch(
	() => props.monthlySeries,
	() => {
		selectedBar.value = null;
	}
);
const evolutionOptions = computed(() =>
	buildEvolutionOptions(props.monthlySeries, {
		metric: props.metric,
		showReturns: props.showReturns,
		labels: { ordered: t('Ordered'), delivered: t('Delivered'), returned: t('Returns') },
		hideYAxis: props.stacked,
		onBarClick: props.stacked ? (bar) => (selectedBar.value = bar) : null,
	})
);
const categoryRows = computed(() => buildCategoryRows(props.categories, { metric: props.metric, unclassifiedLabel: t('Unclassified') }));
const isDonut = computed(() => isDistributionDonut(props.categories));
const distributionOptions = computed(() => buildDistributionOptions(props.categories, { metric: props.metric, unclassifiedLabel: t('Unclassified'), totalLabel: t('Total') }));
</script>

<style scoped lang="scss">
.metric-analysis {
	&__chart {
		min-width: 0;
	}

	&__level {
		max-width: 180px;
	}

	&__list-title {
		display: flex;
		justify-content: flex-end;
	}
}
</style>
